import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  adminStats, adminUsers, adminCourses, adminPayments,
  adminConsultations, adminNotifications, adminTabs
} from "@/mocks/admin";
import AuctionExcelEditor from "@/pages/admin/components/AuctionExcelEditor";
import AuctionExcelUploader from "@/pages/admin/components/AuctionExcelUploader";
import AuctionAnalysisDashboard from "@/pages/admin/components/AuctionAnalysisDashboard";
import AwardedExcelEditor from "@/pages/admin/components/AwardedExcelEditor";
import AwardedExcelUploader from "@/pages/admin/components/AwardedExcelUploader";
import SubmissionsInbox from "@/pages/admin/components/SubmissionsInbox";
import DevLogPanel from "@/pages/admin/components/DevLogPanel";

const months = ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"];

function MiniBarChart({ data, maxVal, color }: { data: number[]; maxVal: number; color: string }) {
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((val, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full rounded-t-sm transition-all"
            style={{ height: `${(val / maxVal) * 100}%`, backgroundColor: color }}
          ></div>
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [searchUser, setSearchUser] = useState("");
  const [searchPayment, setSearchPayment] = useState("");

  const maxRevenue = Math.max(...adminStats.monthlyRevenue);
  const maxGrowth = Math.max(...adminStats.userGrowth);

  const filteredUsers = adminUsers.filter((u) =>
    searchUser ? u.name.includes(searchUser) || u.email.includes(searchUser) : true
  );

  const filteredPayments = adminPayments.filter((p) =>
    searchPayment ? p.user.includes(searchPayment) || p.item.includes(searchPayment) : true
  );

  const statCards = [
    { label: "전체 회원", value: adminStats.totalUsers.toLocaleString() + "명", sub: `신규 ${adminStats.newUsersThisMonth}명`, icon: "ri-user-line", color: "#C4956A" },
    { label: "수강생", value: adminStats.activeStudents.toLocaleString() + "명", sub: `${adminStats.totalCourses}개 과정`, icon: "ri-book-open-line", color: "#10B981" },
    { label: "총 매출", value: "₩" + (adminStats.totalPayments / 10000).toLocaleString() + "만", sub: `이번 달 ₩${(adminStats.paymentsThisMonth / 10000).toLocaleString()}만`, icon: "ri-bank-card-line", color: "#F59E0B" },
    { label: "대기 상담", value: adminStats.pendingConsultations + "건", sub: "신규 신청", icon: "ri-customer-service-2-line", color: "#EF4444" },
  ];

  const statusColor = (status: string) => {
    switch (status) {
      case "완료": return "bg-emerald-50 text-emerald-600";
      case "환불": return "bg-red-50 text-red-600";
      case "예정": case "입찰예정": return "bg-amber-50 text-amber-600";
      case "배정": return "bg-blue-50 text-blue-600";
      case "신청": return "bg-gray-50 text-gray-600";
      case "운영중": return "bg-emerald-50 text-emerald-600";
      case "준비중": return "bg-amber-50 text-amber-600";
      default: return "bg-gray-50 text-gray-600";
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F3F0] font-['Noto_Sans_KR'] flex">
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/30 lg:hidden" onClick={() => setMobileSidebarOpen(false)}></div>
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen bg-[#1C1917] text-[#B8B3AE] transition-all duration-300 overflow-y-auto ${
        mobileSidebarOpen ? "w-64 translate-x-0" : sidebarCollapsed ? "w-20" : "w-64"
      } ${mobileSidebarOpen ? "" : "-translate-x-full lg:translate-x-0"}`}>
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-[#2D2A26]">
          <div className="w-8 h-8 rounded-lg bg-[#C4956A] flex items-center justify-center shrink-0">
            <i className="ri-auction-line text-white"></i>
          </div>
          {(!sidebarCollapsed || mobileSidebarOpen) && (
            <span className="text-white font-bold text-lg whitespace-nowrap">관리자</span>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex ml-auto w-6 h-6 items-center justify-center rounded hover:bg-[#2D2A26] transition-colors"
          >
            <i className={`text-xs ${sidebarCollapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"}`}></i>
          </button>
        </div>

        {/* Nav Items */}
        <nav className="p-3 space-y-1">
          {adminTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => { setActiveTab(tab.key); setMobileSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all ${
                activeTab === tab.key
                  ? "bg-[#C4956A] text-white font-medium"
                  : "text-[#B8B3AE] hover:bg-[#2D2A26] hover:text-white"
              } ${sidebarCollapsed && !mobileSidebarOpen ? "justify-center" : ""}`}
            >
              <i className={`${tab.icon} text-base`}></i>
              {(!sidebarCollapsed || mobileSidebarOpen) && <span className="whitespace-nowrap">{tab.label}</span>}
            </button>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-[#2D2A26]">
          <Link
            to="/"
            className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-[#B8B3AE] hover:bg-[#2D2A26] hover:text-white rounded-lg transition-all ${sidebarCollapsed && !mobileSidebarOpen ? "justify-center" : ""}`}
          >
            <i className="ri-home-line text-base"></i>
            {(!sidebarCollapsed || mobileSidebarOpen) && "사이트로 이동"}
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-[#E8E5E0] px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center hover:bg-[#FAFAF8] transition-colors"
            >
              <i className="ri-menu-line text-[#1C1917]"></i>
            </button>
            <h2 className="text-base font-bold text-[#1C1917]">
              {adminTabs.find((t) => t.key === activeTab)?.label}
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#8C8780] hidden sm:block">
              {new Date().toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", weekday: "short" })}
            </span>
            <div className="w-8 h-8 rounded-full bg-[#C4956A] flex items-center justify-center">
              <span className="text-xs font-bold text-white">관</span>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6">
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map((stat, i) => (
                  <div key={i} className="bg-white rounded-xl border border-[#E8E5E0] p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-medium text-[#8C8780]">{stat.label}</span>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: stat.color + "15" }}>
                        <i className={`${stat.icon} text-sm`} style={{ color: stat.color }}></i>
                      </div>
                    </div>
                    <p className="text-xl font-bold text-[#1C1917] mb-1">{stat.value}</p>
                    <p className="text-xs text-[#8C8780]">{stat.sub}</p>
                  </div>
                ))}
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Revenue Chart */}
                <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
                  <h3 className="text-sm font-bold text-[#1C1917] mb-1">월별 매출</h3>
                  <p className="text-xs text-[#8C8780] mb-4">2026년 (단위: 만원)</p>
                  <MiniBarChart data={adminStats.monthlyRevenue} maxVal={maxRevenue} color="#C4956A" />
                  <div className="flex justify-between mt-2">
                    {months.map((m, i) => (
                      <span key={i} className="text-[10px] text-[#B8B3AE]">{m}</span>
                    ))}
                  </div>
                </div>

                {/* User Growth Chart */}
                <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
                  <h3 className="text-sm font-bold text-[#1C1917] mb-1">월별 신규 회원</h3>
                  <p className="text-xs text-[#8C8780] mb-4">2026년 (단위: 명)</p>
                  <MiniBarChart data={adminStats.userGrowth} maxVal={maxGrowth} color="#10B981" />
                  <div className="flex justify-between mt-2">
                    {months.map((m, i) => (
                      <span key={i} className="text-[10px] text-[#B8B3AE]">{m}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Course Popularity + Recent */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Course Popularity */}
                <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
                  <h3 className="text-sm font-bold text-[#1C1917] mb-4">과정별 수강생</h3>
                  <div className="space-y-3">
                    {adminStats.coursePopularity.map((course, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className="text-xs font-medium text-[#8C8780] w-20 truncate">{course.name}</span>
                        <div className="flex-1 h-3 bg-[#F0EDE8] rounded-full overflow-hidden">
                          <div className="h-full bg-[#C4956A] rounded-full" style={{ width: `${(course.students / 2340) * 100}%` }}></div>
                        </div>
                        <span className="text-xs font-medium text-[#5C5650] w-16 text-right">{course.students.toLocaleString()}명</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recent Payments */}
                <div className="bg-white rounded-xl border border-[#E8E5E0] p-5">
                  <h3 className="text-sm font-bold text-[#1C1917] mb-4">최근 결제</h3>
                  <div className="space-y-2.5">
                    {adminPayments.slice(0, 5).map((p) => (
                      <div key={p.id} className="flex items-center justify-between py-2 border-b border-[#F0EDE8] last:border-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xs font-medium text-[#1C1917] truncate">{p.user}</span>
                          <span className="text-xs text-[#8C8780] hidden sm:block truncate">{p.item}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs font-semibold text-[#1C1917]">{p.amount.toLocaleString()}원</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${statusColor(p.status)}`}>{p.status}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5 border-b border-[#F0EDE8] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-[#1C1917]">회원 목록 ({adminUsers.length}명)</h3>
                <div className="relative w-full sm:w-56">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#8C8780]"></i>
                  <input
                    type="text" placeholder="이름 또는 이메일 검색" value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 text-xs border border-[#E8E5E0] rounded-lg focus:outline-none focus:border-[#C4956A]"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8] text-left">
                      <th className="py-3 px-4 font-medium text-[#8C8780]">회원</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">연락처</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">등급</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">수강</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780] text-right">결제금액</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">가입일</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">최근접속</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8] transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium text-[#1C1917]">{user.name}</span>
                          <span className="text-[#8C8780] ml-2">{user.email}</span>
                        </td>
                        <td className="py-3 px-4 text-[#5C5650]">{user.phone}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.grade === "VIP" ? "bg-amber-50 text-amber-600" : user.grade === "구독" ? "bg-violet-50 text-violet-600" : "bg-gray-50 text-gray-600"}`}>{user.grade}</span>
                        </td>
                        <td className="py-3 px-4 text-[#5C5650]">{user.enrolledCourses}개</td>
                        <td className="py-3 px-4 text-[#1C1917] font-medium text-right">{user.totalPaid.toLocaleString()}원</td>
                        <td className="py-3 px-4 text-[#8C8780]">{user.joinedAt}</td>
                        <td className="py-3 px-4 text-[#8C8780]">{user.lastLogin}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === "courses" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5 border-b border-[#F0EDE8] flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1C1917]">교육 과정 ({adminCourses.length}개)</h3>
                <button className="px-3 py-1.5 text-xs font-medium bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap">
                  <i className="ri-add-line mr-1"></i>과정 추가
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8] text-left">
                      <th className="py-3 px-4 font-medium text-[#8C8780]">과정명</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">유형</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780] text-right">수강생</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780] text-right">가격</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">강의수</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780] text-right">매출</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminCourses.map((course) => (
                      <tr key={course.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8] transition-colors">
                        <td className="py-3 px-4 font-medium text-[#1C1917]">{course.title}</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs bg-[#FAFAF8] text-[#5C5650]">{course.type}</span></td>
                        <td className="py-3 px-4 text-[#5C5650] text-right">{course.students.toLocaleString()}명</td>
                        <td className="py-3 px-4 text-[#1C1917] font-medium text-right">{course.price > 0 ? course.price.toLocaleString() + "원" : "무료"}</td>
                        <td className="py-3 px-4 text-[#5C5650]">{course.lectures}강</td>
                        <td className="py-3 px-4 text-[#1C1917] font-medium text-right">{course.revenue > 0 ? course.revenue.toLocaleString() + "원" : "-"}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(course.status)}`}>{course.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payments Tab */}
          {activeTab === "payments" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5 border-b border-[#F0EDE8] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-[#1C1917]">결제 내역 ({adminPayments.length}건)</h3>
                <div className="relative w-full sm:w-56">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#8C8780]"></i>
                  <input
                    type="text" placeholder="회원명 또는 항목 검색" value={searchPayment}
                    onChange={(e) => setSearchPayment(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 text-xs border border-[#E8E5E0] rounded-lg focus:outline-none focus:border-[#C4956A]"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8] text-left">
                      <th className="py-3 px-4 font-medium text-[#8C8780]">회원</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">유형</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">항목</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780] text-right">금액</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">결제수단</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">상태</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">일시</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((p) => (
                      <tr key={p.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8] transition-colors">
                        <td className="py-3 px-4 font-medium text-[#1C1917]">{p.user}</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs bg-[#FAFAF8] text-[#5C5650]">{p.type}</span></td>
                        <td className="py-3 px-4 text-[#5C5650]">{p.item}</td>
                        <td className="py-3 px-4 text-[#1C1917] font-semibold text-right">{p.amount.toLocaleString()}원</td>
                        <td className="py-3 px-4 text-[#5C5650]">{p.method}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(p.status)}`}>{p.status}</span></td>
                        <td className="py-3 px-4 text-[#8C8780]">{p.date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Auctions Tab */}
          {activeTab === "auctions" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5">
                <AuctionExcelEditor />
              </div>
            </div>
          )}

          {activeTab === "upload" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-6">
                <AuctionExcelUploader />
              </div>
            </div>
          )}

          {activeTab === "analysis" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-6">
                <AuctionAnalysisDashboard />
              </div>
            </div>
          )}

          {/* Awarded Items Tab - 낙찰물건 관리 */}
          {activeTab === "awarded" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5">
                <AwardedExcelEditor />
              </div>
            </div>
          )}

          {/* Awarded Items Upload Tab - 낙찰물건 엑셀 업로드 */}
          {activeTab === "awarded-upload" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-6">
                <AwardedExcelUploader />
              </div>
            </div>
          )}

          {/* Consultations Tab */}
          {activeTab === "inbox" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5">
                <SubmissionsInbox />
              </div>
            </div>
          )}

          {activeTab === "consultations" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5 border-b border-[#F0EDE8] flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1C1917]">상담 관리 ({adminConsultations.length}건)</h3>
                <span className="text-xs text-[#C4956A] font-medium">대기 {adminConsultations.filter(c => c.status === "신청").length}건</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8] text-left">
                      <th className="py-3 px-4 font-medium text-[#8C8780]">회원</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">상담유형</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">일정</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">상태</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">담당자</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminConsultations.map((c) => (
                      <tr key={c.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8] transition-colors">
                        <td className="py-3 px-4 font-medium text-[#1C1917]">{c.user}</td>
                        <td className="py-3 px-4 text-[#5C5650]">{c.type}</td>
                        <td className="py-3 px-4 text-[#5C5650]">{c.date} {c.time}</td>
                        <td className="py-3 px-4"><span className={`px-2 py-0.5 rounded-full text-xs ${statusColor(c.status)}`}>{c.status}</span></td>
                        <td className="py-3 px-4 text-[#5C5650]">{c.assignedTo || "-"}</td>
                        <td className="py-3 px-4">
                          {c.status === "신청" && (
                            <button className="text-xs text-[#C4956A] font-medium hover:text-[#B08050] transition-colors">배정하기</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5 border-b border-[#F0EDE8] flex items-center justify-between">
                <h3 className="text-sm font-bold text-[#1C1917]">알림 발송 내역 ({adminNotifications.length}건)</h3>
                <button className="px-3 py-1.5 text-xs font-medium bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap">
                  <i className="ri-add-line mr-1"></i>알림 발송
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#F0EDE8] bg-[#FAFAF8] text-left">
                      <th className="py-3 px-4 font-medium text-[#8C8780]">제목</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">대상</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780] text-right">발송수</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">방법</th>
                      <th className="py-3 px-4 font-medium text-[#8C8780]">발송일</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminNotifications.map((n) => (
                      <tr key={n.id} className="border-b border-[#F0EDE8] hover:bg-[#FAFAF8] transition-colors">
                        <td className="py-3 px-4 font-medium text-[#1C1917]">{n.title}</td>
                        <td className="py-3 px-4"><span className="px-2 py-0.5 rounded-full text-xs bg-[#FAFAF8] text-[#5C5650]">{n.type}</span></td>
                        <td className="py-3 px-4 text-[#5C5650] text-right">{n.sentCount.toLocaleString()}명</td>
                        <td className="py-3 px-4 text-[#5C5650]">{n.method}</td>
                        <td className="py-3 px-4 text-[#8C8780]">{n.sentAt}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Dev Log Tab */}
          {activeTab === "devlog" && (
            <div className="bg-white rounded-xl border border-[#E8E5E0] overflow-hidden">
              <div className="p-5">
                <DevLogPanel />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}